import { useEffect, useState } from 'react';
import type { CreateRecipeResponse, RecipeImportPrefill } from '@mlc/shared';
import { garderImportDraft, readAiAvailability } from './api';
import { ActiveTimers } from './ActiveTimers';
import { ActiveRecipesBar } from './ActiveRecipesBar';
import { ImportInbox } from './ImportInbox';
import { ImportPasteForm } from './ImportPasteForm';
import { ImportPhotoForm } from './ImportPhotoForm';
import { ImportUrlForm } from './ImportUrlForm';
import { queueLength, replayQueuedMutations } from './offlineQueue';
import { RecipeDetail } from './RecipeDetail';
import { RecipeForm } from './RecipeForm';
import { RecipeList } from './RecipeList';
import { RecipeRead } from './RecipeRead';
import { Login } from './Login';
import { ESPACE_NAVIGATION, Navigation } from './Navigation';
import { ResetPassword } from './ResetPassword';
import { Invitation } from './Invitation';
import { BienDemarrerAuPremierPas } from './BienDemarrer';
import { adopterAppareil } from './memberScope';
import { DeviceModeBanner } from './DeviceMode';
import { chargerSession, oublierSession, useSession } from './session';
import { Settings } from './Settings';
import { ShoppingList } from './ShoppingList';
import { Journal } from './Journal';
import { Collections } from './Collections';
import { MealPlan } from './MealPlan';
import { SharedRecipe } from './SharedRecipe';
import { Link, navigate, paths, useRoute, useSearchParam } from './router';
import { Banner, Button, EmptyState, Icon, LogoInline, PageHeader, buttonClasses } from './ui';
import { useVersion } from './version';

/**
 * Écrans pilotés par l'URL depuis US-13 (`router.tsx`) et non plus par un
 * `useState<View>` : le bouton retour du navigateur remonte le parcours, une
 * recette est partageable par lien, et rafraîchir ne ramène plus à l'accueil.
 *
 * `import-url` / `import-paste` restent, comme avant, des points d'entrée qui
 * alimentent `RecipeForm` avant de s'effacer devant lui — ce ne sont pas des
 * écrans supplémentaires au sens du brief (§6).
 *
 * **Limite assumée : le pré-remplissage d'un import ne survit pas à un
 * rechargement.** `importPrefill` reste en mémoire ; recharger `/nouvelle`
 * affiche un formulaire vide. Le mettre en `sessionStorage` reviendrait à
 * rendre reprenable un import à moitié validé, ce que §3.4 étape 4 ne demande
 * pas (« le résultat est toujours présenté en écran d'édition avant
 * enregistrement », pas « l'import est repris là où il s'est arrêté »).
 */
/**
 * Descend-on dans la page ? (US-105)
 *
 * Le bouton flottant « Ajouter une recette » **recouvrait ce qu'on lisait** :
 * le lien « Chercher avec ce que j'ai » en haut de la bibliothèque, le titre
 * et les métadonnées de la troisième carte ensuite. La réserve de fin de liste
 * d'US-90 dégageait la **dernière** carte, ce qui était le défaut nommé alors ;
 * elle ne pouvait rien contre celles qui passent dessous pendant qu'on défile,
 * et c'est le propre d'un bouton flottant.
 *
 * Il s'efface donc en descendant et revient en remontant — le geste qui suit
 * « je cherche » est « je lis », et celui qui suit « je remonte » est souvent
 * « j'ajoute ». Seuil de huit pixels : sous cette valeur, c'est un tremblement
 * de doigt, pas une intention. Et rien ne se passe dans les cent vingt
 * premiers pixels, où le bouton ne recouvre encore rien.
 */
function useDescente(): boolean {
  const [descend, setDescend] = useState(false);
  useEffect(() => {
    let precedent = window.scrollY;
    function surDefilement(): void {
      const courant = window.scrollY;
      if (Math.abs(courant - precedent) < 8) return;
      setDescend(courant > precedent && courant > 120);
      precedent = courant;
    }
    window.addEventListener('scroll', surDefilement, { passive: true });
    return () => { window.removeEventListener('scroll', surDefilement); };
  }, []);
  return descend;
}

export function App(): React.JSX.Element {
  const route = useRoute();
  const descend = useDescente();
  // Le jeton de réinitialisation (US-53) voyage en paramètre de requête : il ne
  // fait pas partie du chemin, il est le contenu d'un lien reçu par courrier.
  const jetonReinitialisation = useSearchParam('jeton');
  const [refreshKey, setRefreshKey] = useState(0);
  const [justCreatedTitle, setJustCreatedTitle] = useState<string | null>(null);
  const [importPrefill, setImportPrefill] = useState<RecipeImportPrefill | null>(null);
  /**
   * L'entrée du sas ouverte pour correction (US-71).
   *
   * Tant qu'elle est posée, l'écran de validation enregistre **par la route du
   * sas** plutôt que par `POST /api/recipes` : la photo capturée suit, et
   * l'entrée disparaît du même geste. Sans cela, corriger une entrée coûterait
   * sa photo — le navigateur n'en a pas les octets — et laisserait un doublon
   * au sas jusqu'à un nettoyage ultérieur.
   */
  const [sasEnCours, setSasEnCours] = useState<string | null>(null);
  // Espace réservé en haut de la page pour le bandeau de minuteurs actifs
  // (audit UX/UI passe 2, B4 : « le bandeau recouvre le haut de l'écran …
  // sans que rien ne pousse le contenu en dessous »). `ActiveTimers` reste en
  // position fixe (il doit rester visible par-dessus le mode cuisine,
  // `CookMode.tsx`) ; c'est cette page qui compense en réservant sa hauteur
  // réelle plutôt que de deviner une valeur fixe.
  const [timersBannerHeight, setTimersBannerHeight] = useState(0);
  // File d'écritures hors ligne (US-12, §1 CLAUDE.md) : nombre d'écritures en
  // attente de reconnexion, affiché quel que soit l'écran, même principe que
  // les minuteurs actifs (`ActiveTimers`) ci-dessous.
  const [pendingOfflineCount, setPendingOfflineCount] = useState(() => queueLength());
  // Assistance IA configurée ? Détermine l'affichage de l'import photo
  // (US-32). `false` tant qu'on ne sait pas : mieux vaut une entrée qui
  // apparaît qu'une entrée qui disparaît sous le doigt.
  const [aiAvailable, setAiAvailable] = useState(false);
  const [offlineNotice, setOfflineNotice] = useState<string | null>(null);
  /**
   * Session expirée ou absente (US-19). Aucune requête au montage : tant que
   * l'authentification n'est pas configurée côté serveur, rien ne renvoie 401
   * et cet écran n'apparaît jamais. C'est la première réponse 401 qui le
   * déclenche — un appel de vérification systématique retarderait le premier
   * affichage pour la grande majorité des démarrages.
   */
  const [needsLogin, setNeedsLogin] = useState(false);
  /**
   * Aucun membre en base (US-42) : l'application n'expose que la création du
   * premier compte, et l'écran de connexion se change en écran de création.
   * Distinct de `needsLogin` — « il n'y a personne » n'est pas « ce n'est pas
   * vous ».
   */
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  // Mise à jour déployée pendant que l'application est ouverte (US-38).
  const { miseAJourDisponible } = useVersion();
  /** Mode cuisine partagée (US-50) : lecture seule sur le périmètre partagé. */
  const session = useSession();
  const modePartage = session?.device != null;
  // Capacité annoncée par le serveur, jamais supposée (US-53) : sans SMTP les
  // deux routes répondent 404, et un lien vers une impasse est pire que rien.
  const reinitialisationDisponible = session?.password_reset === true;

  // Au montage, **et à chaque fois que les réglages posent ou retirent une clé**
  // (US-69) : la configuration serveur ne changeait pas en cours de session
  // tant qu'elle vivait dans l'environnement, elle se règle maintenant depuis
  // l'application. Sans ce second déclencheur, « la clé posée par l'écran
  // agit sans redémarrage » serait vrai du serveur et faux de l'écran, ce qui
  // revient au même pour qui vient de coller sa clé.
  //
  // Un échec laisse l'entrée masquée — c'est le comportement dégradé attendu,
  // pas une erreur à afficher.
  useEffect(() => {
    const controller = new AbortController();
    function relire(): void {
      readAiAvailability(controller.signal)
        .then(({ available }) => { setAiAvailable(available); })
        .catch(() => { /* sans réponse, on considère l'IA absente */ });
    }
    relire();
    window.addEventListener('mlc:ia', relire);
    return () => { controller.abort(); window.removeEventListener('mlc:ia', relire); };
  }, []);

  // Purge des traces du membre précédent (US-47, vecteurs 18 et 19). Au
  // montage : l'onglet peut avoir été rouvert par quelqu'un d'autre, et le
  // cache hors ligne survit à tout. Aucune requête supplémentaire n'est faite
  // sans nécessité — `readSession` est la seule, et elle est déjà publique.
  useEffect(() => {
    chargerSession()
      .then((etat) => adopterAppareil(etat.member?.id ?? null))
      .catch(() => { /* hors ligne : la purge aura lieu à la prochaine connexion */ });
  }, []);

  useEffect(() => {
    function handleUnauthenticated(): void { setNeedsLogin(true); }
    function handleBootstrap(): void { setNeedsBootstrap(true); setNeedsLogin(true); }
    window.addEventListener('mlc:unauthenticated', handleUnauthenticated);
    window.addEventListener('mlc:bootstrap', handleBootstrap);
    return () => {
      window.removeEventListener('mlc:unauthenticated', handleUnauthenticated);
      window.removeEventListener('mlc:bootstrap', handleBootstrap);
    };
  }, []);

  useEffect(() => {
    function replay(): void {
      replayQueuedMutations()
        .then(({ replayed, remaining }) => {
          setPendingOfflineCount(remaining);
          // Une recette créée hors ligne n'apparaît dans la bibliothèque
          // qu'une fois rejouée (§ limite documentée, DECISIONS.md « US-12 »)
          // : la rafraîchir dès qu'une écriture a effectivement été rejouée.
          if (replayed > 0) setRefreshKey((key) => key + 1);
        })
        .catch(() => { /* nouvel essai à la prochaine reconnexion, cf. offlineQueue.ts */ });
    }

    // Au montage (l'onglet peut avoir été rouvert déjà en ligne, avec une file
    // laissée par une session précédente) et à chaque reconnexion détectée.
    if (typeof navigator === 'undefined' || navigator.onLine) replay();
    window.addEventListener('online', replay);
    return () => { window.removeEventListener('online', replay); };
  }, []);

  // Les bandeaux d'état (« … a été enregistrée », file hors ligne) ne
  // concernent que la bibliothèque : ils s'effacent dès qu'on la quitte,
  // plutôt que de suivre l'utilisateur d'écran en écran.
  // **Les trois écrans de la grille** (US-97) : la bibliothèque, les archives
  // et le fonds Thermomix. Ils montent le même composant, avec le périmètre de
  // leur route — c'est `RecipeList` qui décide de ce qu'il affiche, pas trois
  // copies.
  const onLibrary = route.name === 'library' || route.name === 'archives' || route.name === 'fonds';

  function handleCreated(recipe: CreateRecipeResponse): void {
    setOfflineNotice(null);
    setJustCreatedTitle(recipe.title);
    setImportPrefill(null);
    setSasEnCours(null);
    setRefreshKey((key) => key + 1);
    navigate(paths.library);
  }

  /** La création a été mise en file faute de réseau (US-12) : voir RecipeForm.tsx, `OfflineQueuedError`. */
  function handleQueuedOffline(): void {
    setJustCreatedTitle(null);
    setImportPrefill(null);
    setSasEnCours(null);
    setOfflineNotice('Hors connexion : la recette a été mise en file, elle sera enregistrée à la reconnexion.');
    setPendingOfflineCount(queueLength());
    navigate(paths.library);
  }

  /** Le résultat d'un import (US-02, US-03) rejoint l'écran d'édition existant, jamais enregistré directement (§3.4 étape 4). */
  function handleImported(prefill: RecipeImportPrefill): void {
    setImportPrefill(prefill);
    setSasEnCours(null);
    setJustCreatedTitle(null);
    navigate(paths.newRecipe);
  }

  // Largeur du conteneur : la bibliothèque et la fiche recette profitent de
  // l'espace disponible sur grand écran (§18 mission : « optimise
  // l'utilisation de la largeur »), mais un formulaire de saisie reste
  // volontairement étroit (§18 : « évite les formulaires étirés sur toute la
  // largeur »). Chaque route choisit donc sa propre largeur.
  //
  // **Les réglages et les courses quittent la largeur étroite** (refonte
  // éditoriale). Elle est faite pour la **saisie** — « évite les formulaires
  // étirés sur toute la largeur », §18 de la mission — et ces deux écrans
  // n'en sont pas : les réglages ont un sommaire latéral qui a besoin de sa
  // colonne, et une liste de courses se parcourt rayon par rayon.
  //
  // Le symptôme se voyait avant même l'argument : le conteneur étant centré,
  // leur titre commençait 450 px à droite de la signature Mijoté, alors que
  // tous les autres écrans s'alignent sur elle. Deux écrans sur sept
  // paraissaient appartenir à une autre application.
  const narrowRoute =
    route.name === 'new-recipe' ||
    route.name === 'import-url' ||
    route.name === 'import-paste' ||
    route.name === 'import-photo' ||
    route.name === 'not-found';
  const detailRoute = route.name === 'recipe' || route.name === 'cook' || route.name === 'edit-recipe';

  // Recette partagée (US-33) : rendue **avant** tout le reste, y compris
  // l'écran de connexion. C'est le seul écran destiné à quelqu'un qui n'a pas
  // de carnet ici, et lui demander un mot de passe qu'il n'a pas viderait le
  // partage de son sens.
  if (route.name === 'shared-recipe') {
    return <SharedRecipe token={route.token} />;
  }

  // Mot de passe oublié (US-53) : rendu avant l'écran de connexion, pour la
  // même raison qu'un lien de partage. Demander de se connecter à quelqu'un
  // qui vient précisément parce qu'il ne peut pas est une impasse.
  if (route.name === 'reset-password') {
    return <ResetPassword jeton={jetonReinitialisation === '' ? null : jetonReinitialisation} />;
  }

  // L'arrivée d'un invité (US-103) : rendue avant l'écran de connexion, pour
  // la même raison que la réinitialisation. Elle réutilise le même paramètre
  // `jeton` — un seul nom pour « le contenu d'un lien reçu par courrier »
  // vaut mieux que deux, et les deux écrans ne coexistent jamais.
  if (route.name === 'invitation') {
    return <Invitation jeton={jetonReinitialisation === '' ? null : jetonReinitialisation} />;
  }

  if (needsLogin) {
    return (
      /*
        **L'écran de connexion occupe la page** (refonte éditoriale).

        Il était posé dans le conteneur commun — 1152 px de large, centré
        verticalement — et n'y occupait qu'un rectangle de 290 px collé en
        haut à gauche, le reste de la fenêtre restant vide. C'est le **seul
        écran vu avant d'avoir une raison de rester**, et il ressemblait à un
        formulaire d'administration oublié dans un coin.

        `max-w-form` et un centrage réel, horizontal comme vertical : la
        signature, la promesse, les deux champs. Rien d'autre — il n'y a rien
        d'autre à faire ici.
      */
      <main className="mx-auto flex min-h-full w-full max-w-form flex-col justify-center gap-6 px-6 py-12">
        <Login
          bootstrap={needsBootstrap}
          passwordReset={reinitialisationDisponible}
          onAuthenticated={() => {
            setNeedsLogin(false);
            setNeedsBootstrap(false);
            // Le membre qui vient d'ouvrir la session n'est peut-être pas
            // celui dont les traces sont sur cet appareil.
            oublierSession();
            chargerSession()
              .then((etat) => adopterAppareil(etat.member?.id ?? null))
              .catch(() => { /* sans réponse, on ne purge pas au hasard */ });
            // Rejoue la requête de l'écran courant plutôt que de renvoyer à
            // l'accueil : on revient là où la session avait expiré.
            setRefreshKey((key) => key + 1);
          }}
        />
      </main>
    );
  }

  return (
    <main
      // `max-w-page` (1312 px) et non `max-w-6xl` (1152 px) : la largeur de
      // page est un jeton du design system depuis la refonte éditoriale, et
      // 1152 px laissait, sur un écran de 1440, deux gouttières de 144 px que
      // la grille de la bibliothèque aurait mieux employées.
      //
      // `px-6 py-8` plutôt que `p-6` : le haut d'une page a besoin de plus
      // d'air que ses côtés, sinon la signature semble collée au bord de la
      // fenêtre. `gap-8` pour la même raison — `gap-6` entre l'en-tête et le
      // contenu ne suffisait pas à les séparer.
      className={`mx-auto flex min-h-full max-w-page flex-col gap-8 px-6 py-8 ${ESPACE_NAVIGATION}`}
      // Padding du haut recalculé (B4 ci-dessus) : `p-6` (1.5rem) reste la
      // valeur de base sur les trois autres côtés, seule celle du haut
      // grandit le temps qu'un minuteur soit actif.
      style={timersBannerHeight > 0 ? { paddingTop: `calc(1.5rem + ${timersBannerHeight}px)` } : undefined}
    >
      {/* Les premiers pas (US-104), montés ici pour la même raison que les
          minuteurs : ils ne dépendent d'aucun écran, et le premier écran de
          quelqu'un qui vient d'accepter une invitation n'est pas
          nécessairement la bibliothèque. Le composant décide seul s'il a
          quelque chose à afficher. */}
      <BienDemarrerAuPremierPas />

      {/* Minuteurs actifs (US-09) : montés une seule fois ici, visibles
          depuis n'importe quel écran — y compris par-dessus le mode cuisine
          (`CookMode.tsx`), en position fixe avec un z-index supérieur. */}
      <ActiveTimers onHeightChange={setTimersBannerHeight} />


      {/* Recettes en cuisson (US-35), au même endroit et pour la même raison
          que les minuteurs : on passe d'un plat à son accompagnement depuis
          n'importe quel écran, sans repasser par la bibliothèque. */}
      <ActiveRecipesBar />

      {/* Mode cuisine partagée (US-50) : discret, mais toujours là. Un
          appareil dont on ignore le mode est un appareil sur lequel on se
          croira identifié, et on s'étonnera de ne pas retrouver ses
          recettes. */}
      <DeviceModeBanner />

      {/* Visible depuis n'importe quel écran, même principe que les
          minuteurs actifs ci-dessus (US-12, §1 CLAUDE.md). */}
      {pendingOfflineCount > 0 && (
        <Banner tone="warning">
          {pendingOfflineCount} enregistrement{pendingOfflineCount > 1 ? 's' : ''} en attente de reconnexion.
        </Banner>
      )}

      {/* Une version a été déployée pendant que l'application était ouverte
          (US-38). Le rechargement n'est pas fait d'office : couper une saisie
          de recette ou un mode cuisine en cours pour appliquer une mise à jour
          serait exactement le contraire du service rendu. */}
      {miseAJourDisponible && (
        <Banner tone="info" as="div" className="flex flex-wrap items-center justify-between gap-3">
          <span>Une nouvelle version de Mijoté est disponible.</span>
          <Button type="button" variant="secondary" onClick={() => { window.location.reload(); }}>
            Recharger
          </Button>
        </Banner>
      )}

      {/*
        **Marque et navigation sur une seule ligne en grand écran** (refonte
        éditoriale). Elles occupaient deux rangées superposées : la signature
        seule sur la première, les cinq destinations sur la seconde, puis un
        filet. Trois rangées avant le titre de l'écran, sur une application
        dont le §4 exige trois actions au maximum pour atteindre une première
        étape — et près de 200 px de hauteur mangés avant le moindre contenu,
        y compris sur une tablette posée à plat.

        Le `<nav>` reste **monté une seule fois** (US-56) : c'est le CSS qui le
        déplace — fixé en bas sur mobile, dans cette barre sur grand écran.
        Deux instances produiraient des liens en double dans l'arbre
        d'accessibilité.
      */}
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4 lg:gap-8">
        {/* Le titre ramène à la bibliothèque depuis n'importe quel écran, et
            `<h1>` sur la bibliothèque seulement : ailleurs le titre de la page
            est celui de la recette ou de l'écran en cours. Deux `<h1>` sur un
            même document cassent la navigation par titres.

            **L'en-tête ne porte plus que la marque** (US-56). Les huit boutons
            qu'il portait étaient de deux natures — créer et aller à — sans que
            rien ne les distingue, et quatre d'entre eux vivaient dans un
            `<nav aria-label="Créer une recette">` qui ne créait rien. Les
            destinations sont passées dans `Navigation`, les voies de création
            dans l'écran d'ajout. */}
        {/* **La marque n'est plus un titre** (US-105).

            Elle était un `<h1>` sur la bibliothèque, et `PageHeader` en pose
            un autre pour le titre de l'écran depuis la refonte éditoriale :
            l'accueil, les archives et le fonds portaient donc **deux `h1`**,
            ce qui casse la navigation par titres — précisément le défaut
            qu'US-56 avait entrepris de corriger. Aucun test ne le voyait :
            ceux qui interrogent le titre de niveau 1 le font par son nom, et
            le seul qui l'interroge seul (`recipeRead`) porte sur une fiche,
            où la marque était déjà un paragraphe.

            **Et `text-heading-2` partout**, alors que la bibliothèque lui
            donnait le rang d'un titre d'écran : la marque n'en est plus un.
            C'est aussi ce qu'elle valait déjà sur les onze autres écrans
            (US-57), donc une taille de moins à tenir.

            `lg:text-heading-1` a été essayé, et retiré : ces classes sont
            écrites en dur dans `@layer components` (index.css), **elles ne
            sont pas des utilitaires** et n'ont donc pas de variante. La classe
            compile en silence et ne fait rien — le défaut exact d'US-54, que
            `classesCompilees.test.ts` ne voyait pas parce qu'il ne vérifie que
            les utilitaires porteurs d'un jeton. Un cas y a été ajouté. */}
        <p className="text-heading-2">
          {/* `min-h-touch-min` : le lien qui ramène à la bibliothèque
              depuis n'importe quel écran mesurait 25 px de haut, très en
              dessous du plancher général de 44 px du §4. Il est présent
              partout, donc le défaut l'était aussi. */}
          <Link to={paths.library} className="inline-flex min-h-touch-min items-center text-foreground no-underline">
            <LogoInline />
          </Link>
        </p>
        <Navigation modePartage={modePartage} />
      </header>

      {justCreatedTitle !== null && onLibrary && (
        <Banner tone="success">« {justCreatedTitle} » a été enregistrée.</Banner>
      )}

      {offlineNotice !== null && onLibrary && <Banner tone="warning">{offlineNotice}</Banner>}

      {/* `max-w-form` et non `max-w-2xl`, `max-w-page` et non `max-w-4xl` :
          les largeurs sont des jetons depuis la refonte éditoriale (§ « Largeurs
          de page », index.css). La fiche recette passe de 896 px à la pleine
          largeur de page — elle gère elle-même sa colonne de lecture, et la
          contraindre ici lui interdisait le héros photo et la colonne
          d'ingrédients collante. */}
      <div className={narrowRoute ? 'mx-auto w-full max-w-form' : detailRoute ? 'w-full' : undefined}>
        {/* Les trois voies d'import vivent ici depuis US-56, et non plus dans
            l'en-tête de l'accueil : ce sont des façons de **commencer une
            recette**, pas des destinations. Les mettre au même poids visuel
            que « Journal » et « Semaine » était le défaut d'origine. */}
        {/*
          **Un titre pour les quatre voies de création** (refonte éditoriale).

          Ni l'écran de saisie ni les trois écrans d'import n'avaient de `h1` —
          aucun titre du tout sur celui de saisie, un simple `h2` dans la carte
          des deux autres. Le §4 de CLAUDE.md demande pourtant un plan de
          titres par écran : sans `h1`, ces quatre pages ne se parcourent ni au
          clavier ni en navigation par titres, et l'on arrive sur un champ
          « Titre * » sans savoir ce qu'on est en train de faire.

          Un seul et même titre pour les quatre : ce sont quatre **façons**
          d'ajouter une recette, pas quatre sujets. Les cartes d'import gardent
          leur `h2`, qui dit laquelle.
        */}
        {(route.name === 'new-recipe' || route.name === 'import-url'
          || route.name === 'import-paste' || route.name === 'import-photo') && (
          <PageHeader
            className="mb-6"
            title="Nouvelle recette"
            lede={
              route.name === 'new-recipe'
                ? 'Saisissez-la à la main, ou partez d’une page web, d’un texte collé ou d’une photo.'
                : 'Le résultat vous sera présenté en édition : rien ne s’enregistre sans votre relecture.'
            }
            back={<Link to={paths.library} className={buttonClasses('quiet', 'compact')}><Icon name="fleche-gauche" size={0.9} /> Bibliothèque</Link>}
          />
        )}

        {route.name === 'new-recipe' && !modePartage && (
          <nav aria-label="Partir d’une source" className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-label">Ou partir de</span>
            <Button variant="secondary" size="compact" onClick={() => { navigate(paths.importUrl); }}>
              Une page web
            </Button>
            <Button variant="secondary" size="compact" onClick={() => { navigate(paths.importPaste); }}>
              Un texte collé
            </Button>
            {/* Import photo (US-32) : masqué sans clé IA, plutôt que de laisser
                découvrir l'absence au moment de l'échec (§1). */}
            {aiAvailable && (
              <Button variant="secondary" size="compact" onClick={() => { navigate(paths.importPhoto); }}>
                Une photo
              </Button>
            )}
          </nav>
        )}

        {route.name === 'new-recipe' && (
          <RecipeForm
            initial={importPrefill}
            onCreated={handleCreated}
            onQueuedOffline={handleQueuedOffline}
            onCancel={() => { setImportPrefill(null); setSasEnCours(null); navigate(paths.library); }}
            onViewDuplicate={(id) => { navigate(paths.recipe(id)); }}
            {...(sasEnCours === null
              ? {}
              : { soumettre: (body) => garderImportDraft(sasEnCours, body) })}
          />
        )}

        {route.name === 'import-url' && (
          <ImportUrlForm
            onImported={handleImported}
            onCancel={() => { navigate(paths.library); }}
            onOfferPaste={() => { navigate(paths.importPaste); }}
          />
        )}

        {route.name === 'import-paste' && (
          <ImportPasteForm onImported={handleImported} onCancel={() => { navigate(paths.library); }} />
        )}

        {route.name === 'import-photo' && (
          <ImportPhotoForm onImported={handleImported} onCancel={() => { navigate(paths.library); }} />
        )}

        {/* Le sas (US-71) : le lieu d'attente entre « je tombe sur une
            recette » et « elle est dans ma bibliothèque ». Masqué en mode
            cuisine partagée, comme tout ce qui appartient à quelqu'un. */}
        {route.name === 'import-inbox' && !modePartage && (
          <ImportInbox
            onCorriger={(entree) => {
              setImportPrefill(entree.prefill);
              setSasEnCours(entree.id);
              setJustCreatedTitle(null);
              navigate(paths.newRecipe);
            }}
            onGardee={() => { setRefreshKey((key) => key + 1); }}
          />
        )}

        {(route.name === 'recipe' || route.name === 'cook') && (
          <RecipeRead
            // Remonter le composant quand on change de recette : sans clé, la
            // navigation d'une fiche à l'autre réutiliserait l'état chargé de
            // la précédente le temps de la requête.
            key={route.recipeId}
            recipeId={route.recipeId}
            cooking={route.name === 'cook'}
            onCookingChange={(next) => {
              // `replace` : entrer puis sortir du mode cuisine ne doit pas
              // empiler deux entrées d'historique à repasser au retour.
              navigate(next ? paths.cook(route.recipeId) : paths.recipe(route.recipeId), { replace: true });
            }}
          />
        )}

        {route.name === 'edit-recipe' && (
          <RecipeDetail
            key={route.recipeId}
            recipeId={route.recipeId}
            onBack={() => { navigate(paths.recipe(route.recipeId)); }}
          />
        )}

        {route.name === 'settings' && (
          <Settings onImported={() => { setRefreshKey((key) => key + 1); }} />
        )}

        {route.name === 'shopping' && <ShoppingList />}

        {route.name === 'journal' && <Journal />}

        {/* Les collections ont un écran depuis US-63. Elle reste sous l'onglet
            « Recettes » de la navigation : c'est une façon de lire sa
            bibliothèque, pas une sixième destination. */}
        {route.name === 'collections' && <Collections />}

        {route.name === 'meal-plan' && <MealPlan />}

        {route.name === 'not-found' && (
          // Le même état vide que partout ailleurs, au lieu d'un bloc
          // pointillé réécrit à la main : une page introuvable n'est pas un
          // cas assez spécial pour mériter sa propre forme.
          <EmptyState
            title="Cette page n’existe pas."
            description="Le lien est peut-être incomplet, ou la recette a été supprimée."
            action={
              <Link to={paths.library} className={buttonClasses('primary')}>
                Revenir à la bibliothèque
              </Link>
            }
          />
        )}
      </div>

      {onLibrary && (
        <RecipeList
          refreshKey={refreshKey}
          archived={route.name === 'archives'}
          // Les archives demandent `tout` (US-97) : une recette du fonds qu'on
          // a archivée ne se retrouve nulle part ailleurs — l'écran du fonds
          // ne montre que ce qui ne l'est pas —, et la cacher ici la rendrait
          // irrécupérable, ce que « on archive, on ne supprime jamais »
          // interdit.
          perimetre={route.name === 'fonds' ? 'fonds' : route.name === 'archives' ? 'tout' : 'livre'}
        />
      )}

      {/* Ajouter une recette est une **action**, pas une destination : elle n'a
          donc pas sa place dans la navigation (US-56). Bouton flottant, au
          pouce, et jamais en mode cuisine partagée où il répondrait 404.

          `bottom-24` sur mobile : au-dessus de la barre de navigation, qu'il
          ne doit pas recouvrir. */}
      {route.name === 'library' && !modePartage && (
        // `shadow-overlay` et non `shadow-raised` : un bouton qui flotte
        // au-dessus d'une grille de photos a besoin d'une ombre plus large
        // que celle d'une carte, sans quoi il paraît collé au contenu qui
        // défile dessous. `size="large"` : c'est l'action principale de
        // l'écran principal, elle a droit à la taille qui va avec.
        <Button
          type="button"
          variant="primary"
          size="large"
          // **Rond sur mobile, pilule sur grand écran** (US-105). La pilule
          // faisait deux cents pixels de large : posée sur une liste dont
          // chaque carte occupe toute la largeur, elle en recouvrait le
          // titre. Ronde, elle n'atteint plus qu'un coin — et elle s'efface
          // en descendant (`useDescente` ci-dessus), donc plus rien du tout.
          //
          // `size-14 p-0` écrase le `px-6` de `size="large"` : deux
          // utilitaires de même spécificité se départagent par leur ordre
          // dans la feuille compilée, et `p-0` y arrive après `px-6`. Vérifié
          // plutôt que supposé — c'est le piège nommé dans `RecipeList.tsx`
          // à propos du raccourci de cuisson.
          className={[
            'fixed bottom-24 right-6 z-50 shadow-overlay lg:bottom-8',
            'size-14 rounded-full p-0 lg:size-auto lg:rounded-control lg:px-6',
            'transition-[transform,opacity] duration-(--duration-settle) ease-entrance',
            descend ? 'pointer-events-none translate-y-28 opacity-0' : 'translate-y-0 opacity-100',
          ].join(' ')}
          onClick={() => { setJustCreatedTitle(null); setImportPrefill(null); navigate(paths.newRecipe); }}
        >
          <Icon name="plus" />
          {/* Le libellé reste **dit** partout, et peint à partir de `lg` : un
              bouton rond sans nom accessible s'annonce « bouton », ce qui
              n'aide personne. */}
          <span className="sr-only lg:not-sr-only">Ajouter une recette</span>
        </Button>
      )}

    </main>
  );
}
